import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { rememberedSetting } from './clockFilterState';
import symbolsAPI from '../../../api/symbols';
import { fetchMarketSymbols } from '../marketsSlice';
import { symbolKey } from './clockModel';

// What the Cycle Clock shows (DWLF-509): the watchlist (default), one symbol group, or every tracked market.
// Remembered like the clock's other choices (clockFilterState).
export const WATCHLIST_SCOPE = Object.freeze({ kind: 'watchlist' });

const valid = (s) => s && (s.kind === 'watchlist' || s.kind === 'tracked' || (s.kind === 'group' && typeof s.group === 'string' && s.group));

const remembered = rememberedSetting('cycle-clock-scope', {
  parse: (raw) => {
    const parsed = JSON.parse(raw || 'null');
    if (!valid(parsed)) return WATCHLIST_SCOPE;
    return parsed.kind === 'group' ? { kind: 'group', group: parsed.group } : { kind: parsed.kind };
  },
  serialize: JSON.stringify,
});

export const readClockScope = remembered.read;

export const saveClockScope = (scope) => {
  if (valid(scope)) remembered.save(scope);
};

/**
 * The markets a scope covers: `symbols` undefined for the watchlist (useCycleClockData reads it), null while
 * a list is loading, else the list. "Tracked" is the app's active symbols list (/symbols); a group is its
 * API members (/symbols/groups) that are tracked, the rest named in `untracked` rather than shown as "no
 * reliable cycle". `groups` feeds the picker; `failed` says a list could not be read.
 */
export function useClockUniverse(scope) {
  const dispatch = useDispatch();
  const tracked = useSelector((s) => s.markets.symbols);
  const trackedFetched = useSelector((s) => s.markets.symbolsLastFetched);
  const trackedError = useSelector((s) => s.markets.symbolsError);
  const [groups, setGroups] = useState(null);
  const [groupsFailed, setGroupsFailed] = useState(false);
  const wantsList = scope.kind !== 'watchlist';

  useEffect(() => { if (wantsList) dispatch(fetchMarketSymbols()); }, [dispatch, wantsList]);
  useEffect(() => {
    let cancelled = false;
    symbolsAPI.getGroups()
      .then((g) => { if (!cancelled) setGroups(g); })
      .catch(() => { if (!cancelled) { setGroups([]); setGroupsFailed(true); } });
    return () => { cancelled = true; };
  }, []);

  return useMemo(() => {
    const base = { groups, groupsFailed, untracked: [], failed: null };
    if (!wantsList) return { ...base, symbols: undefined };
    if (trackedError && !trackedFetched) return { ...base, symbols: [], failed: 'Could not load the tracked markets.' };
    if (!trackedFetched) return { ...base, symbols: null };
    if (scope.kind === 'tracked') return { ...base, symbols: tracked || [] };
    if (!groups) return { ...base, symbols: null };
    if (groupsFailed) return { ...base, symbols: [], failed: 'Could not load the groups.' };
    const group = groups.find((g) => g.name === scope.group);
    if (!group) return { ...base, symbols: [] };
    const trackedKeys = new Set((tracked || []).map(symbolKey));
    return {
      ...base,
      symbols: group.members.filter((m) => trackedKeys.has(symbolKey(m))),
      untracked: group.members.filter((m) => !trackedKeys.has(symbolKey(m))),
    };
  }, [wantsList, scope, groups, groupsFailed, tracked, trackedFetched, trackedError]);
}

// "gold_miners" → "Gold miners"; "mag7" → "Mag 7"; "btc_mining" → "BTC mining".
const ACRONYMS = { btc: 'BTC', ai: 'AI', fx: 'FX', us: 'US', etfs: 'ETFs' };
export const groupLabel = (name) => {
  const words = String(name || '').replace(/([a-z])(\d)/gi, '$1_$2').split('_').filter(Boolean)
    .map((w) => ACRONYMS[w.toLowerCase()] || w.toLowerCase());
  const label = words.join(' ');
  return label.charAt(0).toUpperCase() + label.slice(1);
};
